import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Trophy, DollarSign, Globe, ArrowRight, Star, Sparkles,
  FileText, Send, Loader2, BookOpen, Calendar, Building2, User, Copy, Check
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';

const SuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [genForm, setGenForm] = useState({
    period: 'Q4 2025',
    department: 'All',
    person: '',
    country: 'All',
    bom_name: '',
    word_count: 300,
  });

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await api.get('/stories/list');
      setStories(res.data.stories || []);
    } catch {
      // No stories yet - that's fine
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/stories/generate', genForm);
      toast.success('Case study generated!');
      setShowGenerate(false);
      setSelectedStory({ title: res.data.title, content: res.data.content, story_id: res.data.story_id, status: 'draft' });
      fetchStories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate case study');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (storyId) => {
    try {
      await api.post('/stories/publish', { story_id: storyId });
      toast.success('Story published!');
      fetchStories();
      if (selectedStory?.story_id === storyId) {
        setSelectedStory(prev => ({ ...prev, status: 'published' }));
      }
    } catch {
      toast.error('Failed to publish');
    }
  };

  const copyContent = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const departments = ['All', 'Engineering', 'Procurement', 'R&D', 'Production', 'Quality'];
  const countries = ['All', 'USA', 'China', 'Germany', 'India', 'Taiwan', 'Japan'];
  const periods = ['Q1 2025', 'Q2 2025', 'Q3 2025', 'Q4 2025', 'H1 2025', 'H2 2025', 'FY 2025'];

  const sampleCases = [
    {
      id: 'sample-1', title: 'Achieved 23% BOM Cost Reduction in 6 Months',
      company: 'TechVision Electronics', industry: 'Consumer Electronics', location: 'Shenzhen, China',
      challenge: 'Facing margin pressure on IoT device line due to component price volatility and single-source dependencies.',
      solution: 'Implemented AI-powered BOM optimization, identifying 45 FFF alternates and establishing multi-source strategies.',
      results: [{ metric: 'Cost Savings', value: '$2.3M' }, { metric: 'Lead Time', value: '-40%' }, { metric: 'Risk Reduction', value: '85%' }],
      featured: true,
    },
    {
      id: 'sample-2', title: 'Navigated Chip Shortage with Zero Production Delays',
      company: 'AutoDrive Systems', industry: 'Automotive', location: 'Munich, Germany',
      challenge: 'Global semiconductor shortage threatened ADAS module production schedules.',
      solution: 'Used Market Pulse predictions and Risk Radar to anticipate shortages 6 months ahead.',
      results: [{ metric: 'Production', value: '100%' }, { metric: 'Buffer', value: '$5M' }, { metric: 'Alternates', value: '28' }],
      featured: true,
    },
  ];

  return (
    <Layout title="Success Stories" subtitle="Generate AI-powered case studies and share your procurement wins">
      <div className="space-y-6" data-testid="success-stories-page">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-sm py-1 px-3">
              {stories.length} Generated Stories
            </Badge>
          </div>
          <Button
            onClick={() => setShowGenerate(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="generate-story-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate Case Study
          </Button>
        </div>

        <Tabs defaultValue={stories.length > 0 ? "my-stories" : "showcase"} className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-sm">
            <TabsTrigger value="my-stories" data-testid="my-stories-tab">My Stories</TabsTrigger>
            <TabsTrigger value="showcase" data-testid="showcase-tab">Showcase</TabsTrigger>
          </TabsList>

          {/* My Stories Tab */}
          <TabsContent value="my-stories" className="mt-6 space-y-4">
            {loading ? (
              <Card className="bg-white border-gray-200"><CardContent className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" /></CardContent></Card>
            ) : stories.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-16 text-center">
                  <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No stories yet</h3>
                  <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                    Generate your first AI-powered procurement case study based on your platform performance data.
                  </p>
                  <Button onClick={() => setShowGenerate(true)} className="bg-green-600 hover:bg-green-700 text-white">
                    <Sparkles className="h-4 w-4 mr-2" /> Generate First Story
                  </Button>
                </CardContent>
              </Card>
            ) : (
              stories.map(story => (
                <Card
                  key={story.story_id}
                  className="bg-white border-gray-200 shadow-sm hover:shadow-sm transition-all cursor-pointer"
                  data-testid={`story-card-${story.story_id}`}
                  onClick={() => setSelectedStory(story)}
                >
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">{story.title}</h4>
                          <Badge className={story.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {story.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2">{story.content?.substring(0, 200)}...</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{story.period}</span>
                          <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{story.department}</span>
                          <span>{new Date(story.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {story.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-700 border-green-300"
                            onClick={(e) => { e.stopPropagation(); handlePublish(story.story_id); }}
                          >
                            Publish
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); copyContent(story.content, story.story_id); }}
                        >
                          {copiedId === story.story_id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Showcase Tab */}
          <TabsContent value="showcase" className="mt-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Customer Savings', value: '$47M+', icon: DollarSign },
                { label: 'Components Optimized', value: '2.5M+', icon: Sparkles },
                { label: 'Active Customers', value: '500+', icon: Building2 },
                { label: 'Countries Served', value: '45+', icon: Globe },
              ].map((stat, idx) => (
                <Card key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
                  <CardContent className="pt-5 text-center">
                    <stat.icon className="h-7 w-7 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-700">{stat.value}</p>
                    <p className="text-xs text-green-600">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Sample Case Studies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sampleCases.map(study => (
                <Card key={study.id} className="bg-white border-gray-200 shadow-sm hover:shadow-sm transition-all overflow-hidden">
                  <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-white text-base">{study.company}</h3>
                        <p className="text-green-100 text-sm">{study.industry} &middot; {study.location}</p>
                      </div>
                      <Badge className="bg-white/20 text-white"><Trophy className="h-3 w-3 mr-1" />Featured</Badge>
                    </div>
                  </div>
                  <CardContent className="pt-5">
                    <h4 className="text-base font-bold text-gray-900 mb-3">{study.title}</h4>
                    <div className="space-y-2 mb-4">
                      <div><p className="text-xs font-semibold text-gray-500 uppercase">Challenge</p><p className="text-sm text-gray-600">{study.challenge}</p></div>
                      <div><p className="text-xs font-semibold text-gray-500 uppercase">Solution</p><p className="text-sm text-gray-600">{study.solution}</p></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {study.results.map((r, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="text-xl font-bold text-green-600">{r.value}</p>
                          <p className="text-xs text-gray-500">{r.metric}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Story Detail Dialog */}
        <Dialog open={!!selectedStory} onOpenChange={() => setSelectedStory(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-green-600" />
                {selectedStory?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700 leading-relaxed">
              {selectedStory?.content}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => copyContent(selectedStory?.content, 'dialog')}>
                {copiedId === 'dialog' ? <Check className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                Copy
              </Button>
              {selectedStory?.status === 'draft' && (
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handlePublish(selectedStory.story_id)}>
                  Publish
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Generate Dialog */}
        <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-green-600" />
                Generate AI Case Study
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period</Label>
                  <Select value={genForm.period} onValueChange={v => setGenForm({ ...genForm, period: v })}>
                    <SelectTrigger className="mt-1" data-testid="gen-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {periods.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Department</Label>
                  <Select value={genForm.department} onValueChange={v => setGenForm({ ...genForm, department: v })}>
                    <SelectTrigger className="mt-1" data-testid="gen-department"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Country</Label>
                  <Select value={genForm.country} onValueChange={v => setGenForm({ ...genForm, country: v })}>
                    <SelectTrigger className="mt-1" data-testid="gen-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Word Count</Label>
                  <Select value={String(genForm.word_count)} onValueChange={v => setGenForm({ ...genForm, word_count: parseInt(v) })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="200">Short (~200)</SelectItem>
                      <SelectItem value="300">Medium (~300)</SelectItem>
                      <SelectItem value="500">Long (~500)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Key Person (optional)</Label>
                <Input
                  value={genForm.person}
                  onChange={e => setGenForm({ ...genForm, person: e.target.value })}
                  placeholder="e.g., Sarah Chen"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>BOM / Project Name (optional)</Label>
                <Input
                  value={genForm.bom_name}
                  onChange={e => setGenForm({ ...genForm, bom_name: e.target.value })}
                  placeholder="e.g., PCB-2025-Main"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerate(false)} disabled={generating}>Cancel</Button>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="generate-submit-btn"
              >
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Send className="h-4 w-4 mr-2" />Generate</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuccessStories;
